import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Auto-rotating Shorts hero loop — Instagram-Story style preview.
 *
 * Cycles through every short the business has uploaded OR added via URL
 * (YouTube Shorts, regular YouTube, Vimeo) so the "Live Shorts" widget at
 * the top of the public listing works no matter how the owner added them.
 *
 * - Uploaded files render as <video> (auto-advance on `ended`).
 * - YouTube / Vimeo render as <iframe> with background-mode autoplay params,
 *   and we rotate them on a timer since we can't reliably listen for `ended`
 *   across providers without each player SDK.
 */
export default function ShortsHeroLoop({ shorts = [], onTap, brandColor, size = "default" }) {
  // --- Normalize every input shape into a single hero-loop item list ---
  const items = useMemo(() => {
    const out = [];
    for (const raw of shorts || []) {
      const s = typeof raw === "string" ? { url: raw } : raw || {};
      const provider = s.provider || "";
      const isYouTube = provider === "youtube" || provider === "youtube_shorts";
      const isVimeo = provider === "vimeo";

      if (s.embed_url && (isYouTube || isVimeo)) {
        const videoId = s.video_id || "";
        let src = "";
        if (isYouTube && videoId) {
          // background-style autoplay loop (muted by default, no chrome)
          const loopParam = `&loop=1&playlist=${videoId}`;
          src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1&iv_load_policy=3${loopParam}`;
        } else if (isVimeo && videoId) {
          src = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1`;
        } else {
          // Fall back to the stored embed_url; append autoplay/mute hints
          const sep = s.embed_url.includes("?") ? "&" : "?";
          src = `${s.embed_url}${sep}autoplay=1&mute=1&muted=1&controls=0&loop=1`;
        }
        out.push({ kind: "iframe", src, provider });
      } else if (s.url && !isYouTube && !isVimeo) {
        out.push({ kind: "video", src: s.url });
      }
    }
    return out;
  }, [shorts]);

  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const isMobile = size === "mobile";
  const widthClass = isMobile ? "max-w-[260px]" : "max-w-[180px]";

  const current = items[idx];
  const isIframe = current?.kind === "iframe";

  // Keep index in range if the items list shrinks (e.g., short removed)
  useEffect(() => {
    if (idx >= items.length) setIdx(0);
  }, [items.length, idx]);

  // For uploaded videos: autoplay current, advance on end.
  useEffect(() => {
    if (!current || current.kind !== "video") return;
    const v = videoRef.current;
    if (v) {
      v.load();
      const playPromise = v.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {/* autoplay blocked — user must tap */});
      }
    }
  }, [idx, current]);

  // For iframe shorts: we can't listen for `ended` across providers without
  // each player SDK, so rotate on a timer when there are multiple items.
  useEffect(() => {
    if (!current || current.kind !== "iframe" || items.length <= 1) return;
    const t = setTimeout(() => {
      setIdx((i) => (i + 1) % items.length);
    }, 12000); // ~12s per short — enough to get a feel, not too long
    return () => clearTimeout(t);
  }, [idx, current, items.length]);

  const handleVideoEnded = () => {
    setIdx((i) => (i + 1) % items.length);
  };

  if (!items.length) return null;

  const accent = brandColor || "#EC4899";

  // Build an unmuted variant of the current iframe src so the toggle works
  // for YouTube/Vimeo too. Vimeo uses `muted=` and YouTube uses `mute=`.
  const iframeSrc = isIframe
    ? (muted
        ? current.src
        : current.src
            .replace(/([?&])mute=1/g, "$1mute=0")
            .replace(/([?&])muted=1/g, "$1muted=0"))
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.2 }}
      className="relative rounded-2xl overflow-hidden bg-black border-2 shadow-2xl"
      style={{ borderColor: `${accent}80`, boxShadow: `0 10px 40px ${accent}40` }}
      data-testid="shorts-hero-loop"
    >
      <div className={`relative aspect-[9/16] w-full ${widthClass} mx-auto`}>
        {isIframe ? (
          <iframe
            key={`${idx}-${muted ? "m" : "u"}`}
            src={iframeSrc}
            title="Short"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            frameBorder="0"
            className="w-full h-full pointer-events-none"
            data-testid="shorts-loop-iframe"
          />
        ) : (
          <video
            ref={videoRef}
            src={current.src}
            className="w-full h-full object-cover"
            autoPlay
            muted={muted}
            playsInline
            loop={items.length === 1}
            onEnded={handleVideoEnded}
          />
        )}

        {/* Tap-to-toggle-audio overlay (BENEATH the mute icon).
            Previously this overlay called `onTap` which scrolled the page down to the full shorts section —
            users found that disorienting. It now just toggles mute on the currently playing short in place. */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setMuted((m) => !m); }}
          className="absolute inset-0 z-10 group flex items-end justify-center pb-8"
          aria-label={muted ? "Unmute short" : "Mute short"}
          data-testid="shorts-loop-tap"
        >
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-medium px-3 py-1 bg-black/60 rounded-full backdrop-blur flex items-center gap-1">
            {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
            {muted ? "Tap to unmute" : "Tap to mute"}
          </span>
        </button>

        {/* Top label + mute toggle (ABOVE the tap overlay) */}
        <div className="absolute top-2 left-2 right-2 z-20 flex items-center justify-between pointer-events-none">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white border border-white/20"
          >
            ● Live Shorts
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMuted((m) => !m); }}
            className="w-7 h-7 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur flex items-center justify-center pointer-events-auto"
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid="shorts-loop-mute-toggle"
          >
            {muted ? <VolumeX className="w-3.5 h-3.5 text-white" /> : <Volume2 className="w-3.5 h-3.5 text-white" />}
          </button>
        </div>

        {/* Progress dots */}
        {items.length > 1 && (
          <div className="absolute bottom-2 left-2 right-2 z-20 flex gap-1 pointer-events-none">
            {items.map((_, i) => (
              <span
                key={i}
                className="flex-1 h-1 rounded-full transition-all"
                style={{ background: i === idx ? accent : "rgba(255,255,255,0.3)" }}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
